import { useEffect, useMemo, useState } from "react";
import { Button, Drawer, message } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { Commitment, DsmScopeItem } from "design-scope";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { upsertDesignPackageCommitments } from "../shared/bulk-link-design-package-merge";
import { toBulkLinkDesignPackageUpdateInstance } from "../shared/design-scope-create-mappers";
import { DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE } from "../shared/design-scope-messages";
import ScopeDesignPackageCommitmentsEditor, {
  isDesignPackageCommitmentsReady,
  pendingDesignPackageCommitments
} from "./scope-design-package-commitments-editor";
import BulkSelectionViewPanel from "./bulk-selection-view-panel";
import { blankCommitment } from "./scope-item-field";
import type { ProjectEntities } from "../hooks/use-project-entities";
import {
  defaultLodIdFromOptions,
  lodForOptionId,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import "./scope-item.scss";

export type BulkLinkDesignPackageDrawerProps = {
  open: boolean;
  selectedRows: DsmScopeItem[];
  entities: ProjectEntities;
  lodOptions: LevelOfDetailOption[];
  lodOptionsLoading?: boolean;
  projectToken: string;
  onClose: () => void;
  onLinked: () => void | Promise<void>;
};

export default function BulkLinkDesignPackageDrawer(
  props: BulkLinkDesignPackageDrawerProps
) {
  const {
    open,
    selectedRows,
    entities,
    lodOptions,
    lodOptionsLoading,
    projectToken,
    onClose,
    onLinked
  } = props;

  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [inProgress, setInProgress] = useState(false);

  const defaultLodId = useMemo(
    () => defaultLodIdFromOptions(lodOptions),
    [lodOptions]
  );

  const selectionKey = selectedRows.map((r) => r.id).join(",");

  useEffect(() => {
    if (!open) return;
    if (selectedRows.length === 0) {
      onClose();
      return;
    }
    setCommitments([
      blankCommitment(defaultLodId, lodForOptionId(lodOptions, defaultLodId))
    ]);
    // Reset when the drawer opens or the selection identity changes — not on
    // every parent re-render of the selectedRows array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey covers row ids
  }, [open, selectionKey, defaultLodId, onClose]);

  const pending = pendingDesignPackageCommitments(commitments);
  const canLink =
    pending.length > 0 &&
    !entities.loading &&
    isDesignPackageCommitmentsReady(
      commitments,
      lodOptionsLoading,
      lodOptions.length
    );

  const onLink = async () => {
    if (!canLink || inProgress) return;
    if (!projectToken) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }
    setInProgress(true);
    try {
      const feature_instances = selectedRows.map((row) =>
        toBulkLinkDesignPackageUpdateInstance(
          row.id,
          upsertDesignPackageCommitments(row, pending),
          row.bids
        )
      );
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances
        },
        projectToken
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to link design packages.");
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await onLinked();
    } catch (err) {
      console.error(err);
      message.error("Unable to link design packages.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (!inProgress) onClose();
      }}
      size={560}
      closable={false}
      mask={{ closable: false }}
      destroyOnHidden
      className="dsm-edit"
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            data-testid="scope-bulk-link-dp-back"
            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-ciq-primary-900"
            onClick={() => {
              if (!inProgress) onClose();
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <span className="text-base font-medium text-black text-opacity-90">
            Bulk Link to Design Package
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={inProgress}
            data-testid="scope-bulk-link-dp-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canLink || inProgress}
            loading={inProgress}
            onClick={() => void onLink()}
            data-testid="scope-bulk-link-dp-submit"
          >
            Link
          </Button>
        </div>
      }
    >
      <div data-testid="scope-bulk-link-dp-drawer">
        <div className="mb-4 flex items-start gap-2 text-sm text-black text-opacity-50">
          <InfoCircleOutlined className="mt-0.5 shrink-0" />
          <span>
            All selected Design Scope items will be linked to the added Design
            Packages.
          </span>
        </div>

        <BulkSelectionViewPanel items={selectedRows} />

        <ScopeDesignPackageCommitmentsEditor
          commitments={commitments}
          onCommitmentsChange={setCommitments}
          entities={entities}
          lodOptions={lodOptions}
          lodOptionsLoading={lodOptionsLoading}
          active={open}
          addTestId="scope-bulk-link-dp-commit-add"
        />
      </div>
    </Drawer>
  );
}
